import { useEffect, useCallback, useRef } from 'react';

import logger from '@/utils/logger';

export interface KeyboardControlsConfig {
  talkKey?: string;
  interruptKey?: string;
  enablePreventDefault?: boolean;
}

export interface KeyboardControlsState {
  isHoldingTalkKey: boolean;
  lastKeyPressed: string | null;
}

export interface KeyboardControlsCallbacks {
  onTalkKeyDown?: () => void;
  onTalkKeyUp?: () => void;
  onInterruptKey?: () => void;
  onOtherKey?: (key: string, event: KeyboardEvent) => void;
}

const DEFAULT_CONFIG: Required<KeyboardControlsConfig> = {
  talkKey: ' ', // 空格键
  interruptKey: 'Escape',
  enablePreventDefault: true,
};

/**
 * 键盘控制管理 Hook
 * 负责按住说话、快捷键打断等键盘交互
 */
export function useKeyboardControls(
  config: KeyboardControlsConfig = {},
  callbacks: KeyboardControlsCallbacks = {}
): KeyboardControlsState {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const isHoldingRef = useRef(false);
  const lastKeyRef = useRef<string | null>(null);

  const { onTalkKeyDown, onTalkKeyUp, onInterruptKey, onOtherKey } = callbacks;

  // 处理按键按下
  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      const key = event.key;
      lastKeyRef.current = key;

      // 避免在输入框中触发
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        (event.target as HTMLElement)?.contentEditable === 'true'
      ) {
        return;
      }

      // 处理说话键
      if (key === finalConfig.talkKey && !isHoldingRef.current) {
        logger.info('[KeyboardControls] 说话键按下');
        isHoldingRef.current = true;

        if (finalConfig.enablePreventDefault) {
          event.preventDefault();
        }

        onTalkKeyDown?.();
        return;
      }

      // 处理打断键
      if (key === finalConfig.interruptKey) {
        logger.info('[KeyboardControls] 打断键按下');

        if (finalConfig.enablePreventDefault) {
          event.preventDefault();
        }

        onInterruptKey?.();
        return;
      }

      // 处理其他按键
      onOtherKey?.(key, event);
    },
    [
      finalConfig.talkKey,
      finalConfig.interruptKey,
      finalConfig.enablePreventDefault,
      onTalkKeyDown,
      onInterruptKey,
      onOtherKey,
    ]
  );

  // 处理按键释放
  const handleKeyUp = useCallback(
    (event: KeyboardEvent): void => {
      const key = event.key;

      // 避免在输入框中触发
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        (event.target as HTMLElement)?.contentEditable === 'true'
      ) {
        return;
      }

      // 处理说话键释放
      if (key === finalConfig.talkKey && isHoldingRef.current) {
        logger.info('[KeyboardControls] 说话键释放');
        isHoldingRef.current = false;

        if (finalConfig.enablePreventDefault) {
          event.preventDefault();
        }

        onTalkKeyUp?.();
      }
    },
    [finalConfig.talkKey, finalConfig.enablePreventDefault, onTalkKeyUp]
  );

  // 处理窗口失焦（防止按键卡住）
  const handleWindowBlur = useCallback((): void => {
    if (isHoldingRef.current) {
      logger.info('[KeyboardControls] 窗口失焦，释放说话键');
      isHoldingRef.current = false;
      onTalkKeyUp?.();
    }
  }, [onTalkKeyUp]);

  // 处理页面可见性变化
  const handleVisibilityChange = useCallback((): void => {
    if (document.hidden && isHoldingRef.current) {
      logger.info('[KeyboardControls] 页面隐藏，释放说话键');
      isHoldingRef.current = false;
      onTalkKeyUp?.();
    }
  }, [onTalkKeyUp]);

  // 绑定事件监听器
  useEffect(() => {
    logger.debug('[KeyboardControls] 绑定键盘事件监听器');

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      logger.debug('[KeyboardControls] 清理键盘事件监听器');

      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // 清理状态
      isHoldingRef.current = false;
      lastKeyRef.current = null;
    };
  }, [handleKeyDown, handleKeyUp, handleWindowBlur, handleVisibilityChange]);

  // 提供强制释放按键的方法
  const forceRelease = useCallback((): void => {
    if (isHoldingRef.current) {
      logger.info('[KeyboardControls] 强制释放说话键');
      isHoldingRef.current = false;
      onTalkKeyUp?.();
    }
  }, [onTalkKeyUp]);

  // 页面卸载时强制释放
  useEffect(() => {
    const handleBeforeUnload = () => {
      forceRelease();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      forceRelease();
    };
  }, [forceRelease]);

  return {
    isHoldingTalkKey: isHoldingRef.current,
    lastKeyPressed: lastKeyRef.current,
  };
}
